import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { CheckCircle, ShieldAlert, Clock, Info, Loader2 } from 'lucide-react';

const fmt0 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
}).format(n || 0);

/**
 * RMDFlow — Required Minimum Distribution panel.
 *
 * Displayed on the Withdrawals & Loans tab. Pulls from
 *   GET /api/participant/rmd-estimate?still_working_past_rmd=<bool>
 * and renders:
 *   - Applicable RMD age (73 or 75 per SECURE 2.0)
 *   - Years until RMD
 *   - Projected Traditional balance at RMD age
 *   - Estimated first-year RMD amount
 *   - Still-working exception toggle
 *   - Roth exclusion notice
 *   - Penalty disclosure (26 U.S.C. § 4974)
 */
export const RMDFlow = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stillWorking, setStillWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(stillWorking); }, [stillWorking]);

  const load = async (still) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/participant/rmd-estimate', {
        params: { still_working_past_rmd: still },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load RMD estimate.');
    } finally {
      setLoading(false);
    }
  };

  const headerBadge = useMemo(() => {
    if (!data) return null;
    if (data.rmdRequired) {
      return { label: 'RMD ACTIVE', cls: 'bg-red-50 border-red-200 text-red-700' };
    }
    if (stillWorking && data.currentAge >= data.rmdAge) {
      return { label: 'DELAYED — STILL WORKING', cls: 'bg-blue-50 border-blue-200 text-blue-700' };
    }
    return { label: 'FUTURE', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
  }, [data, stillWorking]);

  if (loading && !data) {
    return (
      <div className="panel p-6 animate-pulse" data-testid="rmd-flow-loading">
        <div className="h-4 bg-gray-100 rounded w-48 mb-3" />
        <div className="h-20 bg-gray-50 rounded" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="panel p-5 border-red-200" data-testid="rmd-flow-error">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="rmd-flow">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">
          Required Minimum Distribution (RMD) — SECURE 2.0
        </h2>
        {headerBadge && (
          <span
            className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded border ${headerBadge.cls}`}
            data-testid="rmd-flow-badge"
          >
            {headerBadge.label}
          </span>
        )}
      </div>

      {/* 4 key display cells */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="rmd-flow-cells">
        <div className="panel p-4" data-testid="rmd-age-cell">
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Applicable RMD Age</span>
          <div className="font-mono text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="rmd-age-value">
            {data.rmdAge}
          </div>
          <p className="text-[9px] text-[#9CA3AF] mt-1">
            {data.birthYear <= 1950 ? 'Grandfathered (pre-SECURE 2.0)'
              : data.birthYear <= 1959 ? 'Birth years 1951–1959'
              : 'Birth year ≥ 1960'}
          </p>
        </div>

        <div className="panel p-4" data-testid="years-until-cell">
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Years Until RMD</span>
          <div className="font-mono text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="years-until-value">
            {data.yearsUntilRmd}
          </div>
          <p className="text-[9px] text-[#9CA3AF] mt-1">Current age: {data.currentAge}</p>
        </div>

        <div className="panel p-4" data-testid="projected-trad-cell">
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">
            Projected Traditional at Age {data.rmdAge}
          </span>
          <div className="font-mono text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="projected-trad-value">
            {fmt0(data.projectedTraditional)}
          </div>
          <p className="text-[9px] text-[#9CA3AF] mt-1">
            {data.assumedReturnPct}% assumed annual return
          </p>
        </div>

        <div className="panel p-4 bg-[#1B2A4A] text-white" data-testid="estimated-rmd-cell">
          <span className="text-[10px] text-[#8B95A5] uppercase tracking-wider">First-Year RMD Estimate</span>
          <div className="font-mono text-2xl font-bold mt-1" data-testid="estimated-rmd-value">
            {fmt0(data.estimatedRmd)}
          </div>
          <p className="text-[9px] text-[#8B95A5] mt-1 font-mono">
            ÷ IRS divisor {data.divisor.toFixed(1)}
          </p>
        </div>
      </div>

      {/* Still-working exception toggle */}
      <div className="panel p-4" data-testid="still-working-panel">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={stillWorking}
            onChange={(e) => setStillWorking(e.target.checked)}
            className="h-4 w-4 text-[#1B2A4A] border-gray-300 rounded focus:ring-[#1B2A4A]"
            data-testid="still-working-toggle"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-[#1B2A4A]">
              I am still working at age {data.rmdAge} and want to delay
            </span>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              5 U.S.C. § 8433 still-working exception applies to federal employees actively employed
              at RMD age.
            </p>
          </div>
          {loading && <Loader2 size={14} className="animate-spin text-[#6B7280]" />}
        </label>
        {stillWorking && (
          <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2"
               data-testid="still-working-notice">
            <Clock size={13} className="text-blue-600 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700 leading-relaxed">
              <b>RMD delayed until year of separation from federal service.</b>{' '}
              Your first RMD will be required for the calendar year in which you retire or
              separate, whichever occurs later.
            </p>
          </div>
        )}
      </div>

      {/* Roth exclusion notice */}
      <div className="panel p-4 bg-emerald-50 border-emerald-200" data-testid="roth-exclusion-notice">
        <div className="flex items-start gap-2">
          <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-xs text-emerald-800 leading-relaxed">
            Roth balance of <b data-testid="roth-exclusion-amount">{fmt2(data.rothBalance)}</b>{' '}
            is <b>excluded</b> from RMD calculation. Only Traditional
            (<b data-testid="traditional-rmd-amount">{fmt2(data.traditionalBalance)}</b>) is subject
            to RMD. (SECURE 2.0, effective 2024)
          </p>
        </div>
      </div>

      {/* Penalty disclosure */}
      <div className="panel p-4 bg-red-50 border-red-200" data-testid="rmd-penalty-notice">
        <div className="flex items-start gap-2">
          <ShieldAlert size={14} className="text-red-600 mt-0.5 shrink-0" />
          <p className="text-xs text-red-800 leading-relaxed">
            Failure to take the required distribution results in a{' '}
            <b>25% excise tax</b> on the amount not distributed. Correcting within 2 years
            reduces the penalty to <b>10%</b>. (26 U.S.C. § 4974; SECURE 2.0)
          </p>
        </div>
      </div>
    </div>
  );
};

export default RMDFlow;
